// Skidmore College: public college page (#2652, round 2), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Skidmore's Common Data Set 2025-2026 (cds-2025-26.pdf, linked from the
//    Office of Institutional Research's Common Data Set page): C1
//    applicants/admits/enrollees for Fall 2025 with the residency table, C8
//    test policy for Fall 2027, C21/C22 plans with the Early Decision I and II
//    counts for the Fall 2025 entering class. C21 notes that ED applicants
//    deferred to Regular Decision and later admitted are left out of its ED
//    counts.
//  - Skidmore Admissions, Apply (the dates and deadlines table, standardized
//    tests, portfolios) and Admissions FAQs (no early action; what Early
//    Decision commits you to). The apply page carries no entry year; its
//    financial aid dates (CSS Profile November 8 for ED I, January 15 for ED
//    II and RD) match the Financial Aid Office's "2027-28 dates and
//    deadlines" (November 8, 2026 and January 15, 2027), so it is the page
//    for Fall 2027 entry. Decision dates are Skidmore's own words
//    ("Mid-December" and so on).
//  - Skidmore Bursar, Cost of Attendance: the 2026-27 tuition and required
//    fees, housing and food. Departure: the CDS G1/G5 figures are for
//    2025-2026, so the bursar's 2026-27 billed costs are used. Skidmore
//    publishes no 2026-27 figure for books, personal expenses or travel (its
//    aid FAQ still quotes a 2024-25 estimate), so those show as not published
//    and the total is billed costs only (PARTIAL_COST).
//  - College Scorecard API, school id 195526 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Skidmore Common Data Set 2025-2026',
        url: 'https://www.skidmore.edu/ir/facts/common/cds-2025-26.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Skidmore Admissions, Apply: Dates and Deadlines',
        url: 'https://www.skidmore.edu/admissions/apply/index.php',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Skidmore Admissions, FAQs',
        url: 'https://www.skidmore.edu/admissions/faqs.php',
        read: '2026-10-01',
    },
    bursar: {
        label: 'Skidmore Bursar, Cost of Attendance 2026-27',
        url: 'https://www.skidmore.edu/bursar/cost.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?195526-Skidmore-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'skidmore-college',
    kbId: 'skidmore_college',
    name: 'Skidmore College',
    shortName: 'Skidmore',
    location: 'Saratoga Springs, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Skidmore athletics communications); decoration only.
    branding: {
        primary: '#006A52',
        secondary: '#FFC600',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/skidmore_college_logo.png',
    },
    faq: {
        answer: { text: 'Skidmore College admitted 23.8% of first-year applicants for fall 2025, 2,917 of the 12,273 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Skidmore acceptance rate?', a: 'For fall 2025 entry, Skidmore admitted 2,917 of 12,273 first-year applicants, an acceptance rate of 23.8%, and 692 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the Skidmore early decision acceptance rate?', a: 'For fall 2025, Skidmore admitted 278 of 446 Early Decision I applicants (62.3%) and 81 of 401 Early Decision II applicants (20.2%). These counts leave out early applicants who were deferred to Regular Decision and admitted later.', source: 'cds', where: 'section C21' },
            { q: 'What are Skidmore\'s deadlines for fall 2027?', a: 'Early Decision I is November 1, with decisions in mid-December. Early Decision II and Regular Decision are both January 8, with decisions in mid-February and mid-March. Supporting materials are due within one week of your deadline.', source: 'apply' },
            { q: 'Does Skidmore have early action?', a: 'No. Skidmore offers Early Decision I, Early Decision II and Regular Decision. Early Decision is binding: if Skidmore admits you that way, you withdraw your other applications and enroll.', source: 'faqs' },
            { q: 'Does Skidmore require the SAT or ACT?', a: 'No. Skidmore has been test-optional since 2016. If you send scores, you can self-report them, and Skidmore superscores across your SAT or ACT.', source: 'apply' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 12273,
        admitted: 2917,
        enrolled: 692,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2636, admitted: 742 },
            { group: 'Out-of-state', applied: 4872, admitted: 1886 },
            { group: 'International', applied: 4765, admitted: 289 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Skidmore\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Skidmore has Early Decision I and II and no early action plan. The Common Data Set leaves out early decision applicants who were deferred to Regular Decision and admitted later (1 in Early Decision I, 3 in Early Decision II).',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision I', applied: 446, admitted: 278 },
            { plan: 'Early Decision II', applied: 401, admitted: 81 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Skidmore as a whole, with no supplemental essays. One optional piece:',
        rows: [
            { school: 'Art, music, dance and theater portfolios (optional)', requirement: 'Prospective majors in art, music or dance do not need a portfolio. If you send one, upload it to your Skidmore Applicant Portal within a week of the deadline for your round. Studio art portfolios need at least four drawings from direct observation; dance and theater videos run no longer than three minutes.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 8' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 8', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 8', decision: 'Mid-March', binding: false },
        ],
        note: 'Supporting materials (school report, two teacher recommendations, transcript) are due within one week of the deadline. The CSS Profile is due November 8 for Early Decision I and January 15 for Early Decision II and Regular Decision. Reply deadlines are early January, late February and May 1. Skidmore has no early action plan, and applying is free.',
        source: 'apply',
        binding: 'Early Decision is binding: you can apply to other colleges, but if Skidmore admits you through Early Decision, you withdraw all other applications and commit to enrolling. If you are not admitted, you may be deferred to Regular Decision, with a final decision in mid-March.',
        bindingSource: 'faqs',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Skidmore has been test-optional since 2016 and does not require SAT or ACT scores. If you send them, you can self-report, and Skidmore superscores across your SAT or ACT (the ACT science section is not required). If admitted, you provide verified scores that match by July 15.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        partial: true,
        rows: [
            { item: 'Tuition and required fees', amount: 73590 },
            { item: 'Housing (traditional residence hall)', amount: 11630 },
            { item: 'Food', amount: 8040 },
            { item: 'Books, supplies, personal expenses and travel', amount: null },
        ],
        total: 93260,
        note: 'The $93,260 is what Skidmore bills a student in a traditional residence hall. Skidmore has not published its 2026-27 estimate for books, personal expenses and travel. New students also pay a one-time $150 records fee. Tuition insurance ($365 a semester) and health insurance (about $4,065 a year) are billed unless you waive them.',
        source: 'bursar',
        netPrice: {
            average: 31829,
            byIncome: [
                { income: '$0 to $30,000', amount: 10554 },
                { income: '$30,001 to $48,000', amount: 14116 },
                { income: '$48,001 to $75,000', amount: 15142 },
                { income: '$75,001 to $110,000', amount: 26232 },
                { income: 'Over $110,000', amount: 45040 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Skidmore\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
